import { profileStorageAccountSuffix, profileStorageKey } from './profileStorageKey';
import { isLiteRegisterPlaceholderBirthDate } from './profileLitePlaceholders';

const PROFILE_FIRST_NAME_PREFIX = 'ofrontend.profileDetailsFirstName';
const PROFILE_LAST_NAME_PREFIX = 'ofrontend.profileDetailsLastName';
const PROFILE_OCCUPATION_PREFIX = 'ofrontend.profileDetailsOccupation';
const PROFILE_BIRTH_DATE_PREFIX = 'ofrontend.profileDetailsBirthDate';

/** Legacy keys cleared on logout before per-account storage existed. */
const LEGACY_FIRST_NAME_KEY = 'ofrontend.profileDetailsFirstName';
const LEGACY_LAST_NAME_KEY = 'ofrontend.profileDetailsLastName';
const LEGACY_OCCUPATION_KEY = 'ofrontend.profileDetailsOccupation';
const LEGACY_BIRTH_DATE_KEY = 'ofrontend.profileDetailsBirthDate';

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key)?.trim() || null;
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function readKeyedField(prefix: string, legacyKey: string, loginId?: string | null): string | null {
  const keyed = safeGet(profileStorageKey(prefix, loginId));
  if (keyed) {
    return keyed;
  }
  const legacy = safeGet(legacyKey);
  if (legacy && profileStorageAccountSuffix(loginId) !== 'anonymous') {
    safeSet(profileStorageKey(prefix, loginId), legacy);
    safeRemove(legacyKey);
  }
  return legacy;
}

export type ProfileDetailsHints = {
  firstName: string | null;
  lastName: string | null;
  occupation: string | null;
  birthDate: string | null;
};

export function getStoredProfileDetailsHints(loginId?: string | null): ProfileDetailsHints {
  return {
    firstName: readKeyedField(PROFILE_FIRST_NAME_PREFIX, LEGACY_FIRST_NAME_KEY, loginId),
    lastName: readKeyedField(PROFILE_LAST_NAME_PREFIX, LEGACY_LAST_NAME_KEY, loginId),
    occupation: readKeyedField(PROFILE_OCCUPATION_PREFIX, LEGACY_OCCUPATION_KEY, loginId),
    birthDate: readKeyedField(PROFILE_BIRTH_DATE_PREFIX, LEGACY_BIRTH_DATE_KEY, loginId),
  };
}

/** Cached first + last name for immediate header greeting before GET /profile returns. */
export function joinedProfileDetailsDisplayName(hints: ProfileDetailsHints): string | null {
  const joined = [hints.firstName, hints.lastName].filter(Boolean).join(' ').trim();
  return joined || null;
}

export function persistProfileDetailsHints(
  hints: Partial<{
    firstName: string | null | undefined;
    lastName: string | null | undefined;
    occupation: string | null | undefined;
    birthDate: string | null | undefined;
  }>,
  loginId?: string | null,
): void {
  const firstName = hints.firstName?.trim();
  const lastName = hints.lastName?.trim();
  const occupation = hints.occupation?.trim();
  const birthDate = hints.birthDate?.trim();
  if (firstName) {
    safeSet(profileStorageKey(PROFILE_FIRST_NAME_PREFIX, loginId), firstName);
  }
  if (lastName) {
    safeSet(profileStorageKey(PROFILE_LAST_NAME_PREFIX, loginId), lastName);
  }
  if (occupation) {
    safeSet(profileStorageKey(PROFILE_OCCUPATION_PREFIX, loginId), occupation);
  }
  if ('birthDate' in hints) {
    if (birthDate && !isLiteRegisterPlaceholderBirthDate(birthDate)) {
      safeSet(profileStorageKey(PROFILE_BIRTH_DATE_PREFIX, loginId), birthDate);
    } else {
      // Clear empty / auto-register placeholder DOBs so they don't stick as "trusted".
      safeRemove(profileStorageKey(PROFILE_BIRTH_DATE_PREFIX, loginId));
    }
  }
}

/** @deprecated Profile hints are per-account and intentionally kept across logout. */
export function clearStoredProfileDetailsHints(): void {
  safeRemove(LEGACY_FIRST_NAME_KEY);
  safeRemove(LEGACY_LAST_NAME_KEY);
  safeRemove(LEGACY_OCCUPATION_KEY);
  safeRemove(LEGACY_BIRTH_DATE_KEY);
}
